import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Plus, Trash2, Anchor } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { apiError } from "../api";
import { Card, PageHeader, Button, Input, Field } from "../components/ui";

export default function AdminSettings() {
  const { logoUrl, settings, setSettings } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [invoice, setInvoice] = useState({
    invoice_email: "", invoice_phone: "", invoice_address: "",
    beneficiary_name: "", bank_name: "", bank_account_number: "", ifsc_code: "", pan_no: "", gst_no: "",
  });
  const [ranks, setRanks] = useState([]);
  const [newRank, setNewRank] = useState("");

  useEffect(() => {
    setInvoice({
      invoice_email: settings.invoice_email || "",
      invoice_phone: settings.invoice_phone || "",
      invoice_address: settings.invoice_address || "",
      beneficiary_name: settings.beneficiary_name || "",
      bank_name: settings.bank_name || "",
      bank_account_number: settings.bank_account_number || "",
      ifsc_code: settings.ifsc_code || "",
      pan_no: settings.pan_no || "",
      gst_no: settings.gst_no || "",
    });
  }, [settings]);

  const loadRanks = () => api.get("/ranks").then((r) => setRanks(r.data)).catch(() => {});
  useEffect(() => { loadRanks(); }, []);

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/admin/settings/logo", fd);
      toast.success("Logo updated across the platform");
      setPreview(URL.createObjectURL(file));
      setSettings({ ...settings, logo_url: "/api/settings/logo" });
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const saveInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings/invoice", invoice);
      toast.success("Invoice details saved");
      setSettings({ ...settings, ...invoice });
    } catch (err) { toast.error(apiError(err)); }
  };

  const addRank = async (e) => {
    e.preventDefault();
    if (!newRank.trim()) return;
    try {
      await api.post("/admin/ranks", { name: newRank.trim() });
      toast.success("Rank added");
      setNewRank("");
      loadRanks();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteRank = async (r) => {
    if (!window.confirm(`Delete rank "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/ranks/${r.id}`);
      loadRanks();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Platform branding, invoice details and candidate ranks" />

      <Card className="p-8">
        <h3 className="font-heading text-lg font-bold text-slate-900">MedVerifys Logo</h3>
        <p className="mt-1 text-sm text-slate-500">
          Single configurable logo used on the sidebar, login page and billing invoices.
        </p>
        <div className="mt-6 flex items-center gap-6">
          <div className="flex h-28 w-44 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-2">
            {preview || logoUrl ? (
              <img src={preview || logoUrl} alt="Logo" className="h-full w-full object-contain" data-testid="settings-logo-preview" />
            ) : (
              <ImageIcon className="text-slate-300" size={32} />
            )}
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => { if (e.target.files[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }}
              data-testid="logo-file-input"
            />
            <Button onClick={() => fileRef.current.click()} disabled={busy} data-testid="upload-logo-btn">
              <Upload size={15} /> {busy ? "Uploading..." : "Upload New Logo"}
            </Button>
            <p className="mt-2 text-xs text-slate-400">JPG or PNG recommended</p>
          </div>
        </div>
      </Card>

      <Card className="mt-6 p-8">
        <h3 className="font-heading text-lg font-bold text-slate-900">Invoice Details</h3>
        <p className="mt-1 text-sm text-slate-500">These details appear on every generated invoice along with the logo and watermark.</p>
        <form onSubmit={saveInvoice} className="mt-5 space-y-4">
          <Field label="Email">
            <Input type="email" value={invoice.invoice_email} onChange={(e) => setInvoice({ ...invoice, invoice_email: e.target.value })} data-testid="invoice-email-input" />
          </Field>
          <Field label="Phone Number">
            <Input value={invoice.invoice_phone} onChange={(e) => setInvoice({ ...invoice, invoice_phone: e.target.value })} data-testid="invoice-phone-input" />
          </Field>
          <Field label="Address">
            <Input value={invoice.invoice_address} onChange={(e) => setInvoice({ ...invoice, invoice_address: e.target.value })} data-testid="invoice-address-input" />
          </Field>
          <div className="border-t border-slate-100 pt-4">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Bank & Tax Details (shown on invoice footer)</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Beneficiary">
                <Input value={invoice.beneficiary_name} onChange={(e) => setInvoice({ ...invoice, beneficiary_name: e.target.value })} data-testid="beneficiary-input" />
              </Field>
              <Field label="Bank Name">
                <Input value={invoice.bank_name} onChange={(e) => setInvoice({ ...invoice, bank_name: e.target.value })} data-testid="bank-name-input" />
              </Field>
              <Field label="Bank Account Number">
                <Input value={invoice.bank_account_number} onChange={(e) => setInvoice({ ...invoice, bank_account_number: e.target.value })} data-testid="bank-account-input" />
              </Field>
              <Field label="IFSC Code">
                <Input value={invoice.ifsc_code} onChange={(e) => setInvoice({ ...invoice, ifsc_code: e.target.value })} data-testid="ifsc-input" />
              </Field>
              <Field label="PAN No">
                <Input value={invoice.pan_no} onChange={(e) => setInvoice({ ...invoice, pan_no: e.target.value })} data-testid="pan-input" />
              </Field>
              <Field label="GST No">
                <Input value={invoice.gst_no} onChange={(e) => setInvoice({ ...invoice, gst_no: e.target.value })} data-testid="gst-input" />
              </Field>
            </div>
          </div>
          <Button type="submit" data-testid="save-invoice-settings-btn">Save Invoice Details</Button>
        </form>
      </Card>

      <Card className="mt-6 p-8">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Anchor size={17} className="text-teal" /> Candidate Ranks</h3>
        <p className="mt-1 text-sm text-slate-500">Ranks appear as a dropdown during candidate registration (clinics can also type a custom rank).</p>
        <form onSubmit={addRank} className="mt-5 flex gap-2">
          <Input placeholder="e.g. Pumpman" value={newRank} onChange={(e) => setNewRank(e.target.value)} data-testid="new-rank-input" />
          <Button type="submit" disabled={!newRank.trim()} data-testid="add-rank-btn"><Plus size={15} /> Add</Button>
        </form>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="ranks-list">
          {ranks.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-700">
              {r.name}
              <button onClick={() => deleteRank(r)} className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete rank" data-testid={`delete-rank-${r.id}`}>
                <Trash2 size={13} />
              </button>
            </span>
          ))}
          {ranks.length === 0 && <p className="text-sm text-slate-400">No ranks yet — add the first one.</p>}
        </div>
      </Card>
    </div>
  );
}
